import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Compass, Link2, Plus, XCircle, Building2, School, History } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_PILL = {
  pending: 'bg-amber-50 text-amber-700',
  approved: 'bg-emerald-50 text-emerald-600',
  linked: 'bg-sky-50 text-sky-700',
  rejected: 'bg-red-50 text-red-600',
  withdrawn: 'bg-gray-100 text-gray-500',
};
const STATUS_LABEL = {
  pending: 'Pending review', approved: 'Career created', linked: 'Linked to existing career',
  rejected: 'Rejected', withdrawn: 'Withdrawn by company',
};

/** CareersPOV admin queue for new careers proposed by internship partners. */
export default function InternshipCareerQueue() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [acting, setActing] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/internship-career-requests');
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load the career queue'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <div className="py-16 text-center" data-testid="career-queue-loading"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#9CA3AF]" /></div>;
  }

  const rows = filter === 'all' ? data.requests : data.requests.filter(r => r.status === filter);

  return (
    <div data-testid="internship-career-queue">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-bold text-[#1F2937] font-['Outfit']">Internship career requests</h2>
          <p className="text-xs text-[#6B7280] mt-0.5">
            Companies that could not find their career in the library. Resolving a request links the
            career to the internship so the school can publish it.
          </p>
        </div>
        <div className="flex gap-1.5">
          {['pending', 'all', 'approved', 'linked', 'rejected'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-colors ${
                filter === f ? 'bg-[#F97D6A] text-white' : 'bg-white border border-gray-200 text-[#6B7280]'}`}
              data-testid={`career-queue-filter-${f}`}>
              {f}{f === 'pending' ? ` (${data.counts.pending})` : ''}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 && (
        <p className="text-sm text-[#9CA3AF] bg-white rounded-2xl border border-[#E5E7EB] px-4 py-8 text-center" data-testid="career-queue-empty">
          Nothing in this list.
        </p>
      )}

      <div className="space-y-3">
        {rows.map(r => (
          <div key={r.id} className="bg-white rounded-2xl border border-[#E5E7EB] p-4" data-testid={`career-request-${r.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-bold text-[#1F2937] font-['Outfit'] flex items-center gap-1.5">
                    <Compass className="h-3.5 w-3.5 text-[#F97D6A]" />{r.proposed_name}
                  </p>
                  <Badge className={`border-0 text-[10px] ${STATUS_PILL[r.status]}`} data-testid={`career-request-status-${r.id}`}>
                    {STATUS_LABEL[r.status] || r.status}
                  </Badge>
                </div>
                {r.proposed_description && (
                  <p className="text-xs text-[#4B5563] mt-1.5 leading-relaxed">{r.proposed_description}</p>
                )}
                <p className="text-[11px] text-[#9CA3AF] mt-1.5 flex flex-wrap gap-x-3">
                  <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{r.company_name}</span>
                  <span className="flex items-center gap-1"><School className="h-3 w-3" />{r.school_name}{r.program_name ? ` · ${r.program_name}` : ''}</span>
                  <span>{r.opportunity_title}</span>
                  {r.requested_by_email && <span>{r.requested_by_email}</span>}
                </p>
                {r.status === 'pending' && (r.duplicates || []).length > 0 && (
                  <div className="mt-2.5 bg-[#FAFBFC] rounded-xl px-3 py-2" data-testid={`career-duplicates-${r.id}`}>
                    <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold">Possible existing careers</p>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {r.duplicates.map(d => (
                        <Badge key={d.slug} className="border-0 bg-white text-[#4B5563] text-[11px]" data-testid={`duplicate-${r.id}-${d.slug}`}>
                          {d.name} · {Math.round(d.match * 100)}%
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {r.resolution && (
                  <p className="text-[11px] text-[#4B5563] mt-2 flex items-start gap-1.5" data-testid={`career-resolution-${r.id}`}>
                    <History className="h-3 w-3 mt-0.5 text-[#9CA3AF]" />
                    {r.resolution.career_name
                      ? `Mapped to ${r.resolution.career_name}`
                      : `Rejected — ${r.resolution.reason}`} by {r.resolution.by}
                  </p>
                )}
              </div>
              {r.status === 'pending' && (
                <div className="flex flex-wrap gap-2 shrink-0">
                  <Button size="sm" onClick={() => setActing({ request: r, action: 'create' })}
                    className="bg-emerald-500 text-white hover:bg-emerald-600 rounded-lg text-xs"
                    data-testid={`approve-career-${r.id}`}>
                    <Plus className="h-3.5 w-3.5 mr-1" />Create career
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setActing({ request: r, action: 'link' })}
                    className="rounded-lg text-xs border-gray-200" data-testid={`link-career-${r.id}`}>
                    <Link2 className="h-3.5 w-3.5 mr-1" />Link existing
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setActing({ request: r, action: 'reject' })}
                    className="rounded-lg text-xs text-red-500 hover:bg-red-50" data-testid={`reject-career-${r.id}`}>
                    <XCircle className="h-3.5 w-3.5 mr-1" />Reject
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {acting && (
        <ResolveDialog {...acting} onClose={() => setActing(null)}
          onDone={() => { setActing(null); load(); }} />
      )}
    </div>
  );
}

function ResolveDialog({ request: r, action, onClose, onDone }) {
  const [name, setName] = useState(r.proposed_name);
  const [category, setCategory] = useState('');
  const [stream, setStream] = useState('');
  const [description, setDescription] = useState(r.proposed_description || '');
  const [slug, setSlug] = useState((r.duplicates || [])[0]?.slug || '');
  const [reason, setReason] = useState('');
  const [allowDuplicate, setAllowDuplicate] = useState(false);
  const [careers, setCareers] = useState([]);
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (action !== 'link') return;
    api.get('/careers/options').then(res => setCareers(res.data || [])).catch(() => setCareers([]));
  }, [action]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/internship-career-requests/${r.id}/resolve`, {
        action, name, category, stream, short_description: description,
        career_slug: slug || null, reason: reason.trim() || null, allow_duplicate: allowDuplicate,
      });
      toast.success(action === 'reject' ? 'Request rejected' : 'Career mapped to the internship');
      onDone();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Could not resolve this request';
      toast.error(detail);
      if (detail.includes('already exists')) setAllowDuplicate(false);
    }
    setSaving(false);
  };

  const matches = careers.filter(c => !query.trim()
    || c.name.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg rounded-2xl max-h-[88vh] overflow-y-auto" data-testid="resolve-career-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-['Outfit']">
            {action === 'create' ? 'Create this career' : action === 'link' ? 'Link an existing career' : 'Reject this request'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {r.company_name} · {r.opportunity_title} · proposed as “{r.proposed_name}”
          </DialogDescription>
        </DialogHeader>

        {action === 'create' && (
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-medium text-[#1F2937]">Career name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} className="mt-1 rounded-xl h-10 border-gray-200" data-testid="new-career-name" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-[#1F2937]">Category</Label>
                <Input value={category} onChange={e => setCategory(e.target.value)} placeholder="e.g. Technology" className="mt-1 rounded-xl h-10 border-gray-200" data-testid="new-career-category" />
              </div>
              <div>
                <Label className="text-xs font-medium text-[#1F2937]">Stream</Label>
                <Input value={stream} onChange={e => setStream(e.target.value)} placeholder="Science, Commerce, Arts or Any Stream" className="mt-1 rounded-xl h-10 border-gray-200" data-testid="new-career-stream" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-[#1F2937]">Short description</Label>
              <Textarea value={description} onChange={e => setDescription(e.target.value)} className="mt-1 rounded-xl border-gray-200 min-h-[70px] text-sm resize-none" data-testid="new-career-description" />
            </div>
            {(r.duplicates || []).length > 0 && (
              <label className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 cursor-pointer">
                <input type="checkbox" checked={allowDuplicate} onChange={e => setAllowDuplicate(e.target.checked)} className="mt-0.5 accent-[#F97D6A]" data-testid="allow-duplicate" />
                <span className="text-[11px] text-amber-800 leading-relaxed">
                  Similar careers already exist ({r.duplicates.map(d => d.name).join(', ')}). Tick only if
                  this really is a different career.
                </span>
              </label>
            )}
          </div>
        )}

        {action === 'link' && (
          <div>
            <Label className="text-xs font-medium text-[#1F2937]">Search the career library</Label>
            <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by name" className="mt-1 rounded-xl h-10 border-gray-200" data-testid="link-career-search" />
            <div className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-gray-100 divide-y divide-gray-50">
              {matches.slice(0, 50).map(c => (
                <button key={c.slug} type="button" onClick={() => setSlug(c.slug)}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors ${slug === c.slug ? 'bg-[#FFF0ED] text-[#F06651] font-semibold' : 'text-[#1F2937] hover:bg-[#FAFBFC]'}`}
                  data-testid={`link-option-${c.slug}`}>
                  {c.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {action === 'reject' && (
          <div>
            <Label className="text-xs font-medium text-[#1F2937]">Reason for the company and school</Label>
            <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Explain why this career was not added" className="mt-1 rounded-xl border-gray-200 min-h-[80px] text-sm resize-none" data-testid="reject-career-reason" />
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} className="rounded-xl text-xs text-[#6B7280]">Cancel</Button>
          <Button onClick={submit}
            disabled={saving || (action === 'link' && !slug) || (action === 'reject' && !reason.trim())
              || (action === 'create' && name.trim().length < 3)}
            className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold disabled:opacity-40"
            data-testid="confirm-resolve-career">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            {action === 'create' ? 'Create & link' : action === 'link' ? 'Link career' : 'Reject request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
